import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import { COLORS, TYPOGRAPHY } from "../../../constants/color";
import { useAuth } from "../../../context/authContext";
import AppToast from "../../../components/AppToast";
import axios from "axios";
import { url } from "../../../constants/url";

const PurchaseCredits = () => {
  const { userData, token, refreshUserData } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [fetchingPlans, setFetchingPlans] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastType, setToastType] = useState("success");
  const [toastTitle, setToastTitle] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const [successPlan, setSuccessPlan] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const loadPlans = async () => {
      setFetchingPlans(true);
      setFetchError(null);
      try {
        const response = await axios.get(`${url}/api/client/fir-credits`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data.success) {
          setPlans(response.data.data || []);
        } else {
          throw new Error(response.data.message || "Unable to load FIR plans.");
        }
      } catch (error) {
        console.error("Fetch FIR plans error:", error);
        setFetchError(error.message || "Could not load FIR plans.");
      } finally {
        setFetchingPlans(false);
      }
    };

    if (token) {
      loadPlans();
    }
  }, [token]);

  useEffect(() => {
    if (successPlan) {
      const timer = setTimeout(() => {
        setSuccessPlan(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successPlan]);

  const showToast = (type, title, message) => {
    setToastType(type);
    setToastTitle(title);
    setToastMessage(message);
    setToastOpen(true);
  };

  const handlePurchase = async (plan) => {
    setLoadingPlan(plan._id);

    const basePrice = plan.price;
    const gstAmount = Math.round(basePrice * 0.18);
    const totalAmount = Math.round(basePrice + gstAmount);

    setTimeout(async () => {
      try {
        const response = await axios.post(
          `${url}/api/client/mock-fir-payment`,
          {
            planId: plan._id,
            planName: plan.propertyType,
            amount: totalAmount
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.data.success) {
          refreshUserData();
          localStorage.setItem("purchase_success", JSON.stringify({
            planName: `FIR Plan - ${plan.propertyType}`,
            credits: plan.creditsAllocated || 1,
            clientEmail: response.data.clientEmail || userData?.email,
            purchaseId: response.data.purchaseId,
            timestamp: Date.now()
          }));
          navigate("/projects/add-site-check");
        } else {
          showToast(
            "error",
            "Payment Failed",
            response.data.message || "Something went wrong.",
          );
        }
      } catch (error) {
        console.error("Payment error:", error);
        showToast(
          "error",
          "Payment Failed",
          "Could not complete the purchase. Please try again.",
        );
      } finally {
        setLoadingPlan(null);
      }
    }, 2000);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography sx={{ ...TYPOGRAPHY.H2, color: COLORS.PRIMARY, mb: 1 }}>
        Purchase FIR Credits
      </Typography>
      <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT, mb: 4 }}>
        Select your property type below to purchase a Field Inspection Request
        credit. One credit equals one inspection request.
      </Typography>



      {fetchingPlans ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : fetchError ? (
        <Typography
          sx={{ ...TYPOGRAPHY.BODY_STANDARD, color: COLORS.ERROR, mb: 4 }}
        >
          {fetchError}
        </Typography>
      ) : plans.length === 0 ? (
        <Typography
          sx={{ ...TYPOGRAPHY.BODY_STANDARD, color: COLORS.TEXT, mb: 4 }}
        >
          No FIR credit plans are currently available.
        </Typography>
      ) : (
        <Box sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(3, 1fr)",
            xl: "repeat(3, 1fr)"
          },
          gap: 3,
          width: "100%"
        }}>
          {plans.map((plan) => {
            const basePrice = plan.price;
            const gstAmount = Math.round(basePrice * 0.18);
            const totalPrice = Math.round(basePrice + gstAmount);

            return (
              <Card
                key={plan._id}
                sx={{
                  width: "100%",
                  minHeight: 420,
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 2,
                  transition:
                    "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <CardContent
                  sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
                >
                  <Typography
                    sx={{ ...TYPOGRAPHY.H3, color: COLORS.PRIMARY, mb: 1 }}
                  >
                    {plan.propertyType}
                  </Typography>
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.BODY_STANDARD,
                      color: COLORS.TEXT,
                      mb: 2,
                      flexGrow: 1,
                    }}
                  >
                    {plan.description}
                  </Typography>
                  <Box sx={{ mt: "auto", pt: 2, textAlign: "center" }}>
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.H2,
                        color: COLORS.PRIMARY,
                        fontWeight: 800,
                      }}
                    >
                      ₹{basePrice.toLocaleString("en-IN")}
                    </Typography>
                    <Typography sx={{ fontSize: "11px", color: COLORS.PRIMARY, fontWeight: 600, mt: 0.5 }}>
                      + 18% GST (₹{gstAmount.toLocaleString("en-IN")}) = Total: ₹{totalPrice.toLocaleString("en-IN")}
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Button
                      variant="contained"
                      fullWidth
                      disabled={loadingPlan === plan._id}
                      onClick={() => handlePurchase(plan)}
                      sx={{
                        backgroundColor: COLORS.PRIMARY,
                        py: 1.5,
                        "&:hover": { backgroundColor: COLORS.SECONDARY },
                      }}
                    >
                      {loadingPlan === plan._id ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        `Buy Plan (₹${totalPrice.toLocaleString("en-IN")})`
                      )}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      <AppToast
        open={toastOpen}
        handleClose={() => setToastOpen(false)}
        type={toastType}
        title={toastTitle}
        message={toastMessage}
      />

    </Box>
  );
};

export default PurchaseCredits;
